import { CommonTooltips } from "@mcleod/common";
import { BlurEvent, LookupModelSearchEvent, LookupModelSelectionEvent, Textbox } from "@mcleod/components";
import { CompanySettings, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutTariffManagementTariffLineItem } from "./autogen/AutogenLayoutTariffManagementTariffLineItem";
import { TariffManagementUtil } from "./TariffManagementUtil";

export class TariffManagementTariffLineItem extends AutogenLayoutTariffManagementTariffLineItem {

    private _fromTariffUID: number;
    private _fromTariffID: string;
    private _fromTariffTypeCode: string;
    public set fromTariffUID(value: number) { this._fromTariffUID = value;}
    public get fromTariffUID(): number { return this._fromTariffUID;}
    public set fromTariffID(value: string) { this._fromTariffID = value;}
    public get fromTariffID(): string { return this._fromTariffID;}
    public get getFromTariffTypeCode(): string { return this._fromTariffTypeCode; }
    public set setFromTariffTypeCode(value: string) { this._fromTariffTypeCode = value; }

    override onLoad() {
        TariffManagementUtil.setCurrencyTextboxState(this.textboxCurrency);
        const comparativeOptionEnabled = CompanySettings.get()?.comparative_option_rating;
        this.textboxCompareOptionCode.visible = comparativeOptionEnabled;
    }

    textboxCompareOptionCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.from_compare_option_code = true;
    }

    textboxConveyanceTypeCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_conveyance = true;
        event.filter.is_factor_one = false;
        event.filter.is_factor_two = false;
    }

    textboxQualFactor1BeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_factor_one = true;
        event.filter.is_conveyance = false;
        event.filter.is_factor_two = false;
    }

    textboxQualFactor2BeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_factor_two = true;
        event.filter.is_conveyance = false;
        event.filter.is_factor_one = false;
    }

    textboxGlaUidOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const activeRow = this.activeRow;
            const glaUID = event.target.getDataValue();
            const chargeAlwaysTrueByDefault: number[] = [1, 4, 170, -170, 169, 68, 95, 96, 999, -180];
            const chargeAlwaysFalseByDefault: number[] = [8, 9, 66];
            if (chargeAlwaysTrueByDefault.includes(glaUID)) {
                activeRow.set({ charge_regardless_flag: "1"});
                this.checkboxChargeRegardlessFlag.checked = true;
            }
            else if (chargeAlwaysFalseByDefault.includes(glaUID)) {
                activeRow.set({ charge_regardless_flag: "0"});
                this.checkboxChargeRegardlessFlag.checked = false;
            }
        }
    }

    public populateLookupModelDataInSlider(modelRow: ModelRow<any>, fromCopyTariffLine?: boolean) {
        const chargeTypeLookupModelData = modelRow.getLookupModelData("gla_uid");
        const carrierScacLookupModelData = modelRow.getLookupModelData("carrier_scac");
        if (chargeTypeLookupModelData != null) {
            const data = chargeTypeLookupModelData[0]?.data;
            if (data != null) {
                this.activeRow?.setLookupModelData("gla_uid", { gla_uid: data.gla_uid, short_description: data.short_description });
                this.textboxGlaUid.displayData(this.mainDataSource.activeRow, this?.activeRow?.getLookupModelData[0]?._lookup_gla_uid, 0);
            }
        }
        if (carrierScacLookupModelData != null) {
            const data = carrierScacLookupModelData[0]?.data;
            if (data != null) {
                this.activeRow?.setLookupModelData("carrier_scac", { carrier_scac: data.carrier_scac, "drs_payee.scac": data.carrier_scac });
                this.textboxCarrierScac.displayData(this.mainDataSource.activeRow, this?.activeRow?.getLookupModelData[0]?._lookup_carrier_scac, 0);
            }
        }
        if (fromCopyTariffLine) {
            const tariffCopyLookupModelData = modelRow.getLookupModelData("tar_uid");
            if (tariffCopyLookupModelData != null) {
                const data = tariffCopyLookupModelData[0]?.data;
                if (data != null) {
                    this.activeRow?.setLookupModelData("tar_uid", { tar_uid: data.tar_uid, tariff_name: data.tariff_name });
                    this.textboxCopyToTariff.displayData(this.mainDataSource.activeRow, this?.activeRow?.getLookupModelData[0]?._lookup_tar_uid, 0);
                }
            }
        }
    }

    public addCodeRowsAndTariffRegionsQuickInfo(fromCopyTariffLine?: boolean) {
        const textboxesScreenCodes = [this.textboxMethod, this.textboxConveyanceTypeCode, this.textboxQualFactor1, this.textboxQualFactor2, this.textboxNmfc, this.textboxCompareOptionCode];
        const regionsTextboxes = [this.textboxShipRegName, this.textboxConsRegName];
        textboxesScreenCodes.forEach(textbox => {
            const fieldCode = textbox.getDataValue();
            if (!StringUtil.isEmptyString(fieldCode)) {
                const fieldname = textbox.field === "nmfc" ? "nmfc_class_code" : textbox.field === "conveyance_type_code" ? "tli_conveyance_type_code" : textbox.field;
                const searchFilter = { field_code: fieldCode, field_name: fieldname };
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, searchFilter);
            }
        });
        regionsTextboxes.forEach(textbox => {
            const regionName = textbox.getDataValue();
            if (!StringUtil.isEmptyString(regionName)) {
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/TariffRegionQuickInfo", null, { region_name: regionName });
            }
        });
        if (fromCopyTariffLine) {
            if (!StringUtil.isEmptyString(this.textboxCopyToTariff.getDataValue())) {
                CommonTooltips.setTooltipContentLayout(this.textboxCopyToTariff, null, "lme/dispatch/TariffQuickInfo", null, { tar_uid: this.textboxCopyToTariff.getDataValue() });
            }
        }
    }

    textboxMethodOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxNmfcOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxConveyanceTypeCodeOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxQualFactor1OnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxQualFactor2OnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxCompareOptionCodeOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "ScreenCodes");
    }

    textboxCopyToTariffOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "Tariff");
    }

    textboxConsRegNameOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "TariffRegion");
    }

    textboxShipRegNameOnBlur(event: BlurEvent) {
        this.textboxOnBlur(event, "TariffRegion");
    }

    textboxOnBlur(event: BlurEvent, blurEventFrom?: string) {
        const textbox = event.target as Textbox;
        const value = textbox.getDataValue();
        if (!StringUtil.isEmptyString(value)) {
            if (blurEventFrom === "ScreenCodes") {
                const fieldname = textbox.field === "nmfc" ? "nmfc_class_code" : textbox.field === "conveyance_type_code" ? "tli_conveyance_type_code" : textbox.field;
                const searchFilter = { field_code: value, field_name: fieldname };
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, searchFilter);
            }
            if (blurEventFrom === "Tariff") {
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/TariffQuickInfo", null, { tar_uid: value });
            }
            if (blurEventFrom === "TariffRegion") {
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/TariffRegionQuickInfo", null, { region_name:value });
            }
        } else {
            textbox.text = null;
            textbox["lookupModelData"] = null;
        }
    }

    textboxCopyToTariffBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tariffTypeCode = this.getFromTariffTypeCode === "E" ? "S" : this.getFromTariffTypeCode;
        event.filter.tariff_type_code = tariffTypeCode;
    }

}
